<script setup>
    import MainHeader from './../../components/HeaderComponent.vue';
    import { useRouter } from 'vue-router';
    import { onMounted, reactive } from 'vue';
    import { inject } from 'vue';
    import { ref } from 'vue';
    import { getCurrentInstance } from 'vue';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useSignupStore } from '@/stores/signup/signupStore';
    import BaseInput from './../../components/BaseInputComponent.vue';
    import PasswordInput from './../../components/PasswordInputComponent.vue';
    import Utils from '../../commonProject/helpers/utils.js';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    import i18n from '@/common/i18n';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();
    const loginStore = useLoginStore();
    const signupStore = useSignupStore();

    const form = reactive({
        emailAddress: '',
        password: '',
    });
    const checkPassword = ref(undefined);

    const app = getCurrentInstance();

    const languageStore = useLanguageStore();
    const agreeDisabledFlag = ref(true);
    const regPvcContent = ref('');

    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_P;
    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(async () => {
        // ベンダーIDが取得できない場合は、システムエラーを表示して以降の処理を実施しない。
        if (Utils.isNullEmpty(loginStore.getVendorId) ||
            Utils.isNullEmpty(signupStore.LoginId) ||
            Utils.isNullEmpty(signupStore.ClientType) ||
            Utils.isNullEmpty(signupStore.CmpFmlPwd)) {
            messageDialog('error', i18n.global.t('common.message.venderError'));
            router.push({ name: 'signupApply' });
            return;
        }

        // 個人情報取扱規定取得APIのリクエストパラメータを生成
        const queryParam = {
            VendorId: loginStore.getVendorId,
            LoginId: signupStore.LoginId,
            Lang: languageStore.getLanguageValue,
        };

        // 規定取得APIをコール
        const response = await signupStore.getRegPvcInfoAction(API_URL, queryParam);
        if (response.success) {
            regPvcContent.value = response.responseBody.RegPvcContent;
            agreeDisabledFlag.value = true;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    });

    /**
     * 登録
     */
    async function submitSignup() {
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 自己サインアップ1APIのリクエストボディを生成する。
        const requestBody = {
            VendorId: loginStore.getVendorId,
            ClientType: signupStore.ClientType,
            LoginId: signupStore.LoginId,
            CmpFmlPwd: signupStore.CmpFmlPwd,
            MailAddress: form.emailAddress,
            UserPwd: form.password,
            Lang: languageStore.getLanguageValue,
        };

        // 自己サインアップ1APIをコールする。
        const response = await signupStore.checkUserSignUpAction(API_URL, requestBody);

        // リクエスト結果を受信して、正常応答の例外処理を制御する。
        if (response.success) {
            // 例外処理。APIが応答したが、パスワードがエラーの場合。
            if (response.responseBody.ApiRes.Status === 1) {
                messageDialog('error', i18n.global.t('signupApply.message.message6'));
                return;
            }

            // PKを記憶する。
            signupStore.SignupPk = response.responseBody.SignupPk;

            // 問題が無いので、サインアップ確認画面へ遷移する。
            router.push({ name: 'signupConfirm' });
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // メールアドレス
        if (Utils.isNullEmpty(form.emailAddress)) {
            message += i18n.global.t('signupApply.message.message7') + '\n';
        } else {
            if (Utils.checkLengthOfMailAddress(form.emailAddress)) {
                message += i18n.global.t('signupApply.message.message8') + '\n';
            }
            if (!Utils.isMatchPatternMailAddress(form.emailAddress)) {
                message += i18n.global.t('signupApply.message.message9') + '\n';
            }
        }

        // パスワード
        if (Utils.isNullEmpty(form.password)) {
            message += i18n.global.t('signupApply.message.message10') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(form.password)) {
                message += i18n.global.t('signupApply.message.message11') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(form.password)) {
                message += i18n.global.t('signupApply.message.message12') + '\n';
            }
        }

        // パスワード確認
        if (Utils.isNullEmpty(checkPassword.value)) {
            message += i18n.global.t('signupApply.message.message13') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(checkPassword.value)) {
                message += i18n.global.t('signupApply.message.message14') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(checkPassword.value)) {
                message += i18n.global.t('signupApply.message.message15') + '\n';
            }
        }

        // パスワード入力の不一致。
        if (!Utils.isStringMatch(form.password, checkPassword.value)) {
            message += i18n.global.t('common.message.emptyPasswordUnmatch4') + '\n';
        }

        // 同意チェック
        if (agreeDisabledFlag.value) {
            message += i18n.global.t('signupApply.parts.checkboxLabel1') + '\n';
        }

        if (!Utils.isNullEmpty(message)) {
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    function agreeChange() {
        if (agreeDisabledFlag.value) {
            agreeDisabledFlag.value = false;
        } else {
            agreeDisabledFlag.value = true;
        }
    }

    function backToSignupApply() {
        router.push({ name: 'signupApply' });
    }
</script>

<template>
    <section id="main" class="bg">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <!--ユーザー登録-->
                    <span>{{ $t('signupApply.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents" style="overflow: unset">
                    <section class="form scroll">
                        <dl>
                            <!--メールアドレス-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl6') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="form.emailAddress" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--任意のパスワード-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl7') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                    <li>
                                        <Tooltip>
                                            {{ $t('signupApply.parts.blowoutInformation3') }}
                                        </Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <PasswordInput v-model:input="form.password" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--任意のパスワード(確認)-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl8') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <PasswordInput v-model:input="checkPassword" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--個人情報取扱規定-->
                            <dd>
                                <div id="regPvcContent">{{ regPvcContent }}</div>
                                <input type="checkbox" class="checkbox" id="apply" @change="agreeChange" />
                                <label class="checkbox label" for="apply">{{ $t('signupApply.parts.checkboxLabel1') }}</label>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" class="btnNormal" @click="backToSignupApply" :disabled="disabledStore.isDisabled">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <!--登録-->
                            <button type="button" class="btnPrimary" @click="submitSignup" :disabled="agreeDisabledFlag || disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style scoped>
    #regPvcContent {
        width: 100%;
        height: 400px;
        display: block;
        position: relative;
        background-color: #ffffff;
        border-radius: 4px;
        padding: 8px;
        margin-bottom: 10px;
        box-sizing: border-box;
        overflow-y: auto;
        overflow-x: hidden;
        white-space: pre-wrap;
        word-break: break-all;
    }
</style>
